import { type ReactNode } from "react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/_v2/components/ui/card";
import { Skeleton } from "@/_v2/components/ui/skeleton";
import { cn } from "@/_v2/utils";

export type StatAccent =
  | "brand"
  | "success"
  | "info"
  | "warning"
  | "destructive"
  | "muted";

// Full class names so Tailwind's content scanner picks them up
const ACCENT_CLASSES: Record<StatAccent, { border: string; value: string }> = {
  brand: { border: "border-l-brand", value: "text-brand" },
  success: { border: "border-l-success", value: "text-success" },
  info: { border: "border-l-info", value: "text-info" },
  warning: { border: "border-l-warning", value: "text-warning" },
  destructive: { border: "border-l-destructive", value: "text-destructive" },
  muted: {
    border: "border-l-muted-foreground",
    value: "text-muted-foreground",
  },
};

interface StatCardProps {
  title: string;
  value: ReactNode;
  caption?: string;
  accent?: StatAccent;
  loading?: boolean;
}

export const StatCard = ({
  title,
  value,
  caption,
  accent = "brand",
  loading,
}: StatCardProps) => {
  const accentClasses = ACCENT_CLASSES[accent];

  return (
    <Card className={cn("border-l-4", accentClasses.border)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <div className={cn("text-2xl font-bold", accentClasses.value)}>
            {value}
          </div>
        )}
        {caption ? (
          <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
        ) : null}
      </CardContent>
    </Card>
  );
};
